import { Midi } from "@tonejs/midi"
import { showToastError } from "../style/ShowToast"
import { majorSlow } from "../style/styleSettings/majorSlow"
import { categoriesAndInstruments } from "../sounds/CategoriesAndInstruments";

export const createAndFetchMidi = async (
    setProgress,
    setSong,
    styleSettings
) => {
    const MAX_RETRIES = 2 // Define the maximum number of retries
    let retryCount = 0 // Counter to track the number of attempts
    let progress = 0 // Variable to keep track of the progress
    const data = getPostData(styleSettings)
    console.log("data: ", data)
    const apiAddress = process.env.REACT_APP_API_ADDRESS
    const url = apiAddress //`${apiAddress}?key=0`

    // Function to attempt fetching the MIDI data
    const attemptFetch = async () => {
        // const intervalId = handleInitialProgress(setProgress, progress)
        const { clearProgress } = handleInitialProgress(setProgress, progress)

        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
        })

        const midiBlob = await response.blob()

        const arrayBuffer = await midiBlob.arrayBuffer()

        const fetchedMidi = new Midi(arrayBuffer)
        
        // Parse articulation text events and attach to notes
        attachArticulationToNotes(fetchedMidi, arrayBuffer)
        
        setSong(fetchedMidi, styleSettings)

        clearProgress()
        setProgress(100)

        await delay(1000)
        setProgress(0)
    }

    // Retry loop: Try to fetch MIDI data until successful or retries are exhausted
    while (retryCount <= MAX_RETRIES) {
        try {
            await attemptFetch() // Try to fetch the MIDI data
            break // Exit the loop if successful
        } catch (error) {
            console.error("Error fetching MIDI from backend:", error)
            retryCount++ // Increment the retry counter

            // Show an error message if all retries have been exhausted
            if (retryCount > MAX_RETRIES) {
                showToastError("Failed to fetch MIDI from backend.", 5000)
                await delay(1000)
                setProgress(0)
            }
        }
    }
}

const getPostData = (styleSettings) => {
    const postData = {}

    styleSettings.forEach((setting) => {
        if (setting.inputType === "SLIDER") {
            if (setting.name === "songStructure") {
                postData[setting.name] = getPostDataForSongStructure(setting)
            } else if (setting.name === "root") {
                postData[setting.name] = getPostDataForRoot(setting)
            } else if (setting.name === "key") {
                postData[setting.name] = getPostDataForKey(setting)
            } else if (setting.name === "mode") {
                postData[setting.name] = getPostDataForMode(
                    setting,
                    styleSettings
                )
            } else if (setting.name === "overall extended chord rate") {
                postData["overallExtendedChordRate"] =
                    getPostDataForOverallExtendedChordRate(setting)
            } else if (
                setting.name === "7th chord rate" ||
                setting.name === "9th chord rate" ||
                setting.name === "11th chord rate" ||
                setting.name === "13th chord rate"
            ) {
                postData[getNameOfChordExtension(setting)] =
                    getPostDataForChordExtensions(setting)
            } else {
                postData[setting.name] = setting.value
            }
        } else if (setting.inputType === "RANGE_SLIDER") {
            postData[setting.name] = [setting.lowerValue, setting.higherValue]
        }
    })

    return postData
}

const getNameOfChordExtension = (chordExtensionSetting) => {
    if (chordExtensionSetting.name === "7th chord rate") {
        return "chordRateOf7th"
    } else if (chordExtensionSetting.name === "9th chord rate") {
        return "chordRateOf9th"
    } else if (chordExtensionSetting.name === "11th chord rate") {
        return "chordRateOf11th"
    } else if (chordExtensionSetting.name === "13th chord rate") {
        return "chordRateOf13th"
    }

    return ""
}

const getPostDataForRoot = (keySetting) => {
    let index
    if (keySetting.value === "Random") {
        const nonRandomValues = keySetting.possibleValues.filter(
            (val) => val !== "Random"
        )
        const randomIndex = Math.floor(Math.random() * nonRandomValues.length)
        index =
            keySetting.possibleValues.indexOf(nonRandomValues[randomIndex]) - 1
    } else {
        index = keySetting.possibleValues.indexOf(keySetting.value) - 1
    }

    if (index > 6) {
        index -= 12
    }

    return index
}

const getPostDataForKey = (keySetting) => {
    if (keySetting.value === "Random") {
        const nonRandomValues = keySetting.possibleValues.filter(
            (val) => val !== "Random"
        )
        const randomIndex = Math.floor(Math.random() * nonRandomValues.length)
        return (
            keySetting.possibleValues.indexOf(nonRandomValues[randomIndex]) - 1
        )
    } else {
        return keySetting.possibleValues.indexOf(keySetting.value) - 1
    }
}

const getPostDataForSongStructure = (songStructureSetting) => {
    if (songStructureSetting.value === "Random") {
        const nonRandomValues = songStructureSetting.possibleValues.filter(
            (val) => val !== "Random"
        )
        const randomIndex = Math.floor(Math.random() * nonRandomValues.length)
        return nonRandomValues[randomIndex]
    } else {
        return songStructureSetting.value
    }
}

const getPostDataForOverallExtendedChordRate = (
    overallExtendedChordRateSetting
) => {
    if (overallExtendedChordRateSetting.value === "Random") {
        const nonRandomValues =
            overallExtendedChordRateSetting.possibleValues.filter(
                (val) => val !== "Random"
            )
        const randomIndex = Math.floor(Math.random() * nonRandomValues.length)
        return parseInt(nonRandomValues[randomIndex])
    } else {
        return parseInt(overallExtendedChordRateSetting.value)
    }
}

const getPostDataForChordExtensions = (chordExtensionSetting) => {
    if (chordExtensionSetting.value === "Random") {
        const nonRandomValues = chordExtensionSetting.possibleValues.filter(
            (val) => val !== "Random"
        )
        const randomIndex = Math.floor(Math.random() * nonRandomValues.length)
        return parseInt(nonRandomValues[randomIndex])
    } else {
        return parseInt(chordExtensionSetting.value)
    }
}

const getPostDataForMode = (modeSetting, styleSettings) => {
    let keyValue = styleSettings.find((s) => s.name === "key").value

    if (keyValue === "Random") {
        keyValue = styleSettings.find((s) => s.name === "key").value
        const keyNameInPossibleValues = styleSettings.find(
            (s) => s.name === "key"
        ).possibleValues[keyValue]
        const modeOptionsForRandomSelectedKey = modeSetting.possibleValues.find(
            (p) => p.key === keyNameInPossibleValues
        ).values
        const randomIndex = Math.floor(
            Math.random() * modeOptionsForRandomSelectedKey.length
        )
        return randomIndex
    } else {
        const modeOptions = modeSetting.possibleValues.find(
            (p) => p.key === keyValue
        )
        return modeOptions.values.indexOf(modeSetting.value)
    }
}

/**
 * Parse articulation text events from MIDI and attach to note objects
 * Text events with format "ARTICULATION:<articulation_name>" are parsed
 * and the articulation is attached to the following note(s)
 */
const attachArticulationToNotes = (midi, arrayBuffer) => {
    // Default articulation if none specified
    const defaultArticulation = "sustain"
    
    // Parse text events from raw MIDI buffer
    const articulationMap = parseTextEventsFromMidiBuffer(arrayBuffer, midi.header.ticksPerQuarter)
    
    // Process each track
    midi.tracks.forEach((track, trackIndex) => {
        // Set default articulation on all notes first
        track.notes.forEach(note => {
            note.articulation = defaultArticulation
        })
        
        // Apply articulations from parsed events
        // Match articulation events to notes by time (within small tolerance)
        track.notes.forEach(note => {
            // Find the closest articulation event before or at this note's time
            const noteTimeTicks = Math.round(note.time * midi.header.ticksPerQuarter)
            
            // Look for articulation events at this time or slightly before
            for (let i = noteTimeTicks; i >= Math.max(0, noteTimeTicks - 10); i--) {
                if (articulationMap[trackIndex] && articulationMap[trackIndex][i]) {
                    note.articulation = articulationMap[trackIndex][i]
                    break
                }
            }
        })
    })
}

/**
 * Parse text events from raw MIDI buffer
 * Returns a map: trackIndex -> { tickTime: articulation }
 */
const parseTextEventsFromMidiBuffer = (arrayBuffer, ticksPerQuarter) => {
    const articulationMap = {}
    const view = new DataView(arrayBuffer)
    let offset = 0
    
    // Skip MIDI header (14 bytes: "MThd" + header)
    if (view.getUint32(0) === 0x4D546864) { // "MThd"
        offset = 14
    }
    
    // Parse tracks
    let trackIndex = 0
    while (offset < arrayBuffer.byteLength) {
        // Check for track header "MTrk"
        if (view.getUint32(offset) === 0x4D54726B) { // "MTrk"
            offset += 4
            const trackLength = view.getUint32(offset)
            offset += 4
            
            const trackStart = offset
            const trackEnd = offset + trackLength
            
            // Parse events in this track
            let currentTick = 0
            let runningStatus = 0
            
            while (offset < trackEnd) {
                // Read delta time (variable length)
                let deltaTime = 0
                let byte
                do {
                    byte = view.getUint8(offset++)
                    deltaTime = (deltaTime << 7) | (byte & 0x7F)
                } while (byte & 0x80)
                
                currentTick += deltaTime
                
                // Read status byte
                const status = view.getUint8(offset)
                
                // Check for meta event (0xFF)
                if (status === 0xFF) {
                    offset++
                    const metaType = view.getUint8(offset++)
                    
                    // Text event (0x01)
                    if (metaType === 0x01) {
                        // Read text length (variable length)
                        let textLength = 0
                        do {
                            byte = view.getUint8(offset++)
                            textLength = (textLength << 7) | (byte & 0x7F)
                        } while (byte & 0x80)
                        
                        // Read text
                        const textBytes = new Uint8Array(arrayBuffer, offset, textLength)
                        const text = new TextDecoder('utf-8').decode(textBytes)
                        offset += textLength
                        
                        // Check if it's an articulation event
                        if (text.startsWith("ARTICULATION:")) {
                            const articulation = text.substring(13) // Remove "ARTICULATION:" prefix
                            if (!articulationMap[trackIndex]) {
                                articulationMap[trackIndex] = {}
                            }
                            articulationMap[trackIndex][currentTick] = articulation
                        }
                    } else {
                        // Skip other meta events
                        let metaLength = 0
                        do {
                            byte = view.getUint8(offset++)
                            metaLength = (metaLength << 7) | (byte & 0x7F)
                        } while (byte & 0x80)
                        offset += metaLength
                    }
                } else if (status >= 0x80 && status < 0xF0) {
                    // MIDI channel message - skip
                    runningStatus = status
                    if ((status & 0xF0) === 0xC0 || (status & 0xF0) === 0xD0) {
                        offset++ // Program change or channel pressure (1 byte)
                    } else {
                        offset += 2 // Note on/off, etc. (2 bytes)
                    }
                } else if (status < 0x80) {
                    // Running status - use previous status
                    if (runningStatus) {
                        if ((runningStatus & 0xF0) === 0xC0 || (runningStatus & 0xF0) === 0xD0) {
                            offset++ // 1 byte
                        } else {
                            offset += 2 // 2 bytes
                        }
                    } else {
                        offset++ // Skip if no running status
                    }
                } else {
                    // System message - skip
                    offset++
                }
            }
            
            offset = trackEnd
            trackIndex++
        } else {
            offset++
        }
    }
    
    return articulationMap
}

export const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const handleInitialProgress = (setProgress) => {
    let initialProgress = 0
    let delay = 75 // Initial delay
    let timeoutId = null // Store the timeout ID

    const increaseProgress = () => {
        initialProgress += 1
        setProgress(initialProgress)

        // Adjust the delay based on progress thresholds
        if (initialProgress >= 95) {
            delay = 250 // Slowest delay after 95
        } else if (initialProgress >= 90) {
            delay = 175 // Slower delay after 90
        } else if (initialProgress >= 85) {
            delay = 125 // Slower delay after 85
        } else if (initialProgress >= 80) {
            delay = 100 // Slower delay after 80
        }

        // Continue increasing progress until 100
        if (initialProgress < 100) {
            timeoutId = setTimeout(increaseProgress, delay)
        }
    }

    // Start the progress increase
    timeoutId = setTimeout(increaseProgress, delay)

    // Function to stop the progress
    const clearProgress = () => {
        clearTimeout(timeoutId)
    }

    return { clearProgress }
}

export const updateKeySetting = (currentSettings, newKeyValue) => {
    const currentModeSetting = findSettingByName(currentSettings, "mode")
    const modesOfNewKey = getModesByKey(newKeyValue)
    const currentModeValue = currentModeSetting.value
    const modesOfCurrentKey = getModesByKey(currentModeSetting.key)
    const indexOfCurrentModeValue = modesOfCurrentKey.indexOf(currentModeValue)
    const newModeValue =
        modesOfNewKey[indexOfCurrentModeValue] || modesOfNewKey[0]

    return currentSettings.map((setting) => {
        if (setting.name === "key") {
            return { ...setting, value: newKeyValue }
        } else if (setting.name === "mode") {
            return { ...setting, value: newModeValue, key: newKeyValue }
        }
        return setting
    })
}
    
export const updateInstrumentSetting = (styleSettings, settingName, newValue) => {
    console.log('updateInstrumentSetting')
    return styleSettings.map((setting) => {
        if (setting.name !== settingName) {
            return setting;
        }

        const newCategory = categoriesAndInstruments.find((category) => 
            category.instrument.includes(newValue)
        )?.category || setting.category; 

        return {
            ...setting,
            value: newValue,
            category: newCategory,
        };
    });
};

const findSettingByName = (settings, name) =>
    settings.find((setting) => setting.name === name)

const getModesByKey = (key) => {
    const modeSetting = majorSlow.find(
        (setting) => setting.name === "mode"
    )
    return modeSetting.possibleValues.find((pv) => pv.key === key)?.values || []
}
